import axios from 'axios'
import { getDefaultStore } from 'jotai'
import type { HttpResponse } from '@/types/api'
import { sessionAtom } from '@/store/auth.atom'

interface RefreshResponse {
    access_token?: string
    accessToken?: string
    refreshToken?: string
    permissions?: string[]
}

interface LegacyHttpResponse<T> {
    responseCode?: string
    responseMessage?: string
    responseData?: T
}

function unwrapResponse<T>(
    payload: HttpResponse<T> | LegacyHttpResponse<T>,
): T {
    if ('result' in payload && payload.result !== undefined) {
        return payload.result
    }

    if ('responseData' in payload && payload.responseData !== undefined) {
        return payload.responseData
    }

    throw new Error('Invalid refresh response format')
}

const refreshInstance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: Number(import.meta.env.VITE_API_TIMEOUT) || 30_000,
    withCredentials: true,
})

const REFRESH_PATH = import.meta.env.VITE_AUTH_REFRESH_PATH || '/v2/auth/refresh-token'

let isRefreshing = false
let pendingRefresh: Promise<string> | null = null

interface QueuedItem<T = unknown> {
    resolve: (value: T) => void
    reject: (reason: unknown) => void
    retry: () => Promise<T>
}

const queue: QueuedItem[] = []

function flushQueue(error: unknown | null) {
    while (queue.length) {
        const item = queue.shift()!
        if (error) {
            item.reject(error)
        } else {
            item.retry().then(item.resolve, item.reject)
        }
    }
}

export async function refreshAccessToken(): Promise<string> {
    if (pendingRefresh) return pendingRefresh

    pendingRefresh = (async () => {
        const store = getDefaultStore()
        const currentSession = store.get(sessionAtom)

        const res = await refreshInstance.post<
            HttpResponse<RefreshResponse> | LegacyHttpResponse<RefreshResponse>
        >(REFRESH_PATH, undefined)

        const responseData = unwrapResponse<RefreshResponse>(res.data)
        const newToken = responseData.accessToken || responseData.access_token

        if (!newToken) {
            throw new Error('Cannot extract new access token from response')
        }

        if (!currentSession) {
            throw new Error('Cannot refresh access token without an active session')
        }

        const nextSession = {
            ...currentSession,
            permissions: responseData.permissions ?? currentSession.permissions,
            accessToken: newToken,
        }

        store.set(sessionAtom, nextSession)
        return newToken
    })()

    try {
        return await pendingRefresh
    } catch (error: any) {
        const message = error?.response?.data?.responseMessage || error?.message || 'Đã xảy ra lỗi khi làm mới token.';
        throw new Error(message);
    } finally {
        pendingRefresh = null
    }
}

export async function withRefresh<T>(fn: () => Promise<T>): Promise<T> {
    if (isRefreshing) {
        return new Promise<T>((resolve, reject) => {
            queue.push({
                resolve: resolve as (v: unknown) => void,
                reject,
                retry: fn,
            })
        })
    }

    isRefreshing = true
    try {
        await refreshAccessToken()
        const result = await fn()
        flushQueue(null)
        return result
    } catch (e) {
        flushQueue(e)
        throw e
    } finally {
        isRefreshing = false
    }
}